// frontend/src/components/CommentsSection.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "api";
import { StarFour, X } from "phosphor-react";
import { isIOS } from "utils/platform";
import MentionTextarea from "./MentionTextarea";
import { DEFAULT_CIRCLE_IMAGE, DEFAULT_PROFILE_IMAGE } from "../utils/blank-image";

if (isIOS) document.documentElement.classList.add("ios");

const humanizeError = (err) => {
  const d = err?.response?.data;
  if (!d) return "Something went wrong.";
  if (typeof d.detail === "string") return d.detail;
  if (Array.isArray(d.detail) && d.detail.length) return d.detail[0]?.msg || "Validation error.";
  return "Request failed.";
};

const fmtCommentTime = (value) => {
  if (!value) return "";

  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return "";

  const secondsAgo = Math.max(
    0,
    Math.floor((Date.now() - dt.getTime()) / 1000)
  );

  if (secondsAgo < 60) return "now";

  const minutesAgo = Math.floor(secondsAgo / 60);
  if (minutesAgo < 60) return `${minutesAgo}m`;

  const hoursAgo = Math.floor(minutesAgo / 60);
  if (hoursAgo < 24) return `${hoursAgo}h`;

  const daysAgo = Math.floor(hoursAgo / 24);
  if (daysAgo < 7) return `${daysAgo}d`;

  const weeksAgo = Math.floor(daysAgo / 7);
  if (weeksAgo < 52) return `${weeksAgo}w`;

  const yearsAgo = Math.floor(daysAgo / 365);
  return `${yearsAgo}y`;
};

const Avatar = ({ src, alt }) => (
  <img
    src={src || DEFAULT_PROFILE_IMAGE}
    alt={alt || "avatar"}
    className="w-7 h-7 rounded-full object-cover"
    loading="lazy"
    decoding="async"
  />
);

// Mentions: support @"Full Name" (quoted, up to 60 chars) or unquoted up to TWO words.
const TOKEN_RE =
  /(^|[\s])@("([^"\n]{1,60})"|([A-Za-z0-9_.]+(?: [A-Za-z0-9_.]+)?))(?=$|\s|[.,!?;:)\]])/g;

const CommentsSection = ({
    activityId,
    currentUser,
    className = "",
    onCountChange,
    readOnly = false,
    initialComments = [],
  }) => {
  const [comments, setComments] = useState(initialComments);
  const [value, setValue] = useState("");
  const [posting, setPosting] = useState(false);
  const [deletingIds, setDeletingIds] = useState({});
  const [error, setError] = useState("");
  const [expanded, setExpanded] = useState(false);

  const [mobileDeleteFor, setMobileDeleteFor] = useState(null);
  const longPressTimerRef = useRef(null);

  // --- Stars modal state ---
  const [starModalFor, setStarModalFor] = useState(null); // comment id
  const [starUsers, setStarUsers] = useState([]); // [{id, name, username, profile_pic_url}]
  const [starLoading, setStarLoading] = useState(false);
  const [starErr, setStarErr] = useState("");

  const nav = useNavigate();

  // a stable id for the input so we can focus it
  const textareaId = `comment-input-${activityId}`;

  const handleReply = (user) => {
    const raw = user?.username || user?.name || "user";
    const handle = String(raw).replace(/\s+/g, ""); // "Rachel Green" -> "RachelGreen"
    const mention = `@${handle} `;

    setExpanded(true);
    setValue((prev) => (prev.startsWith(mention) ? prev : `${mention}${prev.replace(/^@\S+\s*/, "")}`));

    // focus + scroll into view (mobile/iOS safe)
    setTimeout(() => {
      const el = document.getElementById(textareaId);
      if (el) {
        el.focus();
        try {
          const len = el.value?.length ?? 0;
          el.setSelectionRange?.(len, len);
        } catch {}
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }, 0);
  };

  // resolver: label/handle -> user id (built from following list)
  const [idLookup, setIdLookup] = useState({});

  useEffect(() => {
    let cancelled = false;
    const loadFollowing = async () => {
      try {
        const token = localStorage.getItem("token");
        const meId = localStorage.getItem("user_id");
        const headers = token ? { Authorization: `Bearer ${token}` } : undefined;
        const url = meId ? `/connections/following/${meId}` : "/connections/following/me";
        const { data } = await api.get(url, { headers });
        const list = Array.isArray(data) ? data : [];
        const map = {};
        for (const u of list) {
          const id = u.id;
          const label =
            u.name || u.display_name || u.username || (id ? `User #${id}` : "User");
          const handle = u.username || "";
          if (label) map[String(label).toLowerCase()] = id;
          if (handle) map[String(handle).toLowerCase()] = id;
        }
        if (!cancelled) {
          setIdLookup((prev) => ({
            ...prev,
            ...map, // <-- merge instead of overwrite
          }));
        }
      } catch {
        /* ignore */
      }
    };
    loadFollowing();
    return () => {
      cancelled = true;
    };
  }, []);

  // Always include currentUser in idLookup so mentions of their own name resolve
  useEffect(() => {
    if (!currentUser?.id) return;

    setIdLookup((prev) => {
      const next = { ...prev };

      const id = currentUser.id;

      const label =
        currentUser.name ||
        currentUser.display_name ||
        currentUser.username ||
        (id ? `User #${id}` : "");

      if (label) next[String(label).toLowerCase()] = id;

      if (currentUser.username) {
        next[String(currentUser.username).toLowerCase()] = id;
      }

      if (currentUser.display_name) {
        next[String(currentUser.display_name).toLowerCase()] = id;
      }

      return next;
    });
  }, [currentUser]);

  // Add numeric fallback so "@123" resolves to id 123
  const resolveId = (labelOrHandle) => {
    const key = String(labelOrHandle || "").toLowerCase();
    if (/^\d+$/.test(key)) return Number(key);
    return idLookup[key];
  };

  const token = localStorage.getItem("token");
  const authHeader = token ? { Authorization: `Bearer ${token}` } : undefined;

  const load = async () => {
    setError("");
    try {
      const res = await api.get(`/comments/${activityId}`, { headers: authHeader });
      const list = Array.isArray(res.data) ? res.data : [];
      setComments(list);
      onCountChange?.(list.length);

      // hydrate lookup from authors present in these comments
      try {
        const extra = {};
        for (const c of list) {
          const u = c?.user;
          if (u?.id) {
            if (u.username) extra[String(u.username).toLowerCase()] = u.id;
            if (u.name) extra[String(u.name).toLowerCase()] = u.id;
            if (u.display_name) extra[String(u.display_name).toLowerCase()] = u.id;
          }
        }
        if (Object.keys(extra).length) {
          setIdLookup((m) => ({ ...m, ...extra }));
        }
      } catch { /* ignore */ }
    } catch {
      setError("Failed to load comments.");
    }
  };

  useEffect(() => {
    if (readOnly) {
      setComments(Array.isArray(initialComments) ? initialComments : []);
      return;
    }

    if (activityId) {
      setExpanded(false);
      load();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activityId, readOnly]);

  const visibleComments = useMemo(
    () => (expanded ? comments : comments.slice(0, 2)),
    [expanded, comments]
  );

  const handlePost = async (e) => {
    e.preventDefault();
    setError("");
    const trimmed = value.trim();
    if (!trimmed) return setError("Please write a comment first.");
    if (!token) return setError("Please log in to comment.");

    setPosting(true);
    try {
      const res = await api.post(
        `/comments/${activityId}`,
        { content: trimmed },
        { headers: { ...authHeader, "Content-Type": "application/json" } }
      );
      setComments((list) => [...list, res.data]);
      setValue("");
      onCountChange?.((n) => (Number(n) || 0) + 1);
      window.dispatchEvent(new CustomEvent("comment-added", { detail: { activityId } }));
    } catch (e2) {
      setError(humanizeError(e2));
    } finally {
      setPosting(false);
    }
  };

  const handleDelete = async (commentId) => {
    if (!token) return;

    // find the commenter id before mutating state
    const c = (comments || []).find((x) => x.id === commentId);
    const commenterId =
      c?.user_id ?? c?.author_id ?? c?.user?.id ?? currentUser?.id ?? null;

    setDeletingIds((m) => ({ ...m, [commentId]: true }));
    try {
      await api.delete(`/comments/${commentId}`, { headers: authHeader });

      // update local state
      setComments((list) => list.filter((c) => c.id !== commentId));
      onCountChange?.((n) => Math.max(0, (Number(n) || 0) - 1));

      // notify ActivityCard so it can trim the collapsed avatar row
      window.dispatchEvent(
        new CustomEvent("comment-deleted", {
          detail: { activityId, userId: commenterId },
        })
      );
    } catch (e2) {
      setError(humanizeError(e2));
    } finally {
      setDeletingIds((m) => {
        const copy = { ...m };
        delete copy[commentId];
        return copy;
      });
    }
  };

  // --- Stars: toggle + modal open ---
  const toggleStar = async (c) => {
    if (!token) return setError("Please log in to star comments.");
    const already = !!c.starred_by_me;
    try {
      if (already) {
        await api.delete(`/comments/${c.id}/star`, { headers: authHeader });
      } else {
        await api.post(`/comments/${c.id}/star`, {}, { headers: authHeader });
      }
      setComments((list) =>
        list.map((x) =>
          x.id === c.id
            ? {
                ...x,
                starred_by_me: !already,
                star_count: Math.max(0, (x.star_count || 0) + (already ? -1 : 1)),
              }
            : x
        )
      );

      // live-update modal list if it's open for this comment
      if (starModalFor === c.id) {
        if (!already && currentUser?.id) {
          const me = {
            id: currentUser.id,
            name: currentUser.name || currentUser.username || "User",
            username: currentUser.username,
            profile_pic_url: currentUser.profile_pic_url,
          };
          setStarUsers((prev) => {
            const exists = (prev || []).some((u) => u?.id === me.id);
            return exists ? prev : [me, ...(Array.isArray(prev) ? prev : [])];
          });
        } else if (already && currentUser?.id) {
          setStarUsers((prev) => (Array.isArray(prev) ? prev.filter((u) => u?.id !== currentUser.id) : []));
        }
      }
    } catch (e2) {
      setError(humanizeError(e2));
    }
  };

  const fetchStarUsers = async (commentId) => {
    const token = localStorage.getItem("token");
    const headers = token ? { Authorization: `Bearer ${token}` } : undefined;
    const endpoints = [
      `/comments/${commentId}/stars`,
      `/comments/${commentId}/star/users`,
      `/comments/${commentId}/starred`,
    ];
    for (const ep of endpoints) {
      try {
        const r = await api.get(ep, { headers });
        if (Array.isArray(r.data)) return r.data;
        if (Array.isArray(r.data?.users)) return r.data.users;
        if (Array.isArray(r.data?.items)) return r.data.items;
      } catch {
        /* try next */
      }
    }
    throw new Error("Could not load stars.");
  };

  const openStars = async (commentId) => {
    setStarModalFor(commentId);
    setStarErr("");
    setStarLoading(true);
    try {
      const users = await fetchStarUsers(commentId);
      setStarUsers(Array.isArray(users) ? users : []);
    } catch (e) {
      setStarErr(e?.message || "Failed to load stars.");
      setStarUsers([]);
    } finally {
      setStarLoading(false);
    }
  };

  const closeStars = () => {
    setStarModalFor(null);
    setStarUsers([]);
    setStarErr("");
    setStarLoading(false);
  };

  // --- MentionLink: ensures navigation to /profile/{id}. If id missing, resolve via /users/search
  const MentionLink = ({ id, handle, children }) => {
    const token = localStorage.getItem("token");
    const headers = token ? { Authorization: `Bearer ${token}` } : undefined;

    const onClick = async (e) => {
      if (id) return;
      e.preventDefault();

      const q = String(handle || "").trim();
      if (!q) return;

      try {
        const r = await api.get(`/users/search`, { params: { q }, headers });
        const arr = Array.isArray(r.data) ? r.data : [];

        const exact =
          arr.find(
            (u) =>
              u?.username &&
              String(u.username).toLowerCase() === q.toLowerCase()
          ) ||
          arr.find(
            (u) =>
              String(u.name || "").toLowerCase() === q.toLowerCase()
          );

        if (exact?.id) {
          nav(`/profile/${exact.id}`);
        }
      } catch {
        // no-op
      }
    };

    if (readOnly) {
      return (
        <span className="font-semibold text-blue-700">
          {children}
        </span>
      );
    }

    if (id) {
      return (
        <Link
          to={`/profile/${id}`}
          className="font-semibold text-blue-700 hover:underline cursor-pointer"
          title={`View user #${id}`}
        >
          {children}
        </Link>
      );
    }

    return (
      <button
        type="button"
        onClick={onClick}
        className="font-semibold text-blue-700 hover:underline cursor-pointer bg-transparent p-0 m-0"
        title={`View @${handle}`}
      >
        {children}
      </button>
    );
  };

  const renderWithMentions = (text) => {
    const s = String(text || "");
    const nodes = [];
    let last = 0;
  
    for (const m of s.matchAll(TOKEN_RE)) {
      const start = m.index ?? 0;
      const end = start + m[0].length;
  
      if (start > last) nodes.push(s.slice(last, start));
  
      const prefix = m[1] || "";
      if (prefix) nodes.push(prefix);
  
      let raw = (m[3] || m[4] || "").trim();
      if (!raw) {
        nodes.push("@");
        last = end;
        continue;
      }
  
      let display = raw;    // what we show as the mention text
      let trailing = "";    // any extra text after the mention within this match
      let id;
  
      // Quoted case is m[3]; unquoted is m[4]
      const isQuoted = !!m[3];
  
      if (!isQuoted && raw.includes(" ")) {
        // Unquoted two-word case, e.g. "rachel park" or "dory hiii"
        const parts = raw.split(/\s+/, 2);
        const first = parts[0];
        const rest = raw.slice(first.length); // includes the leading space before second word
  
        const fullKey = raw.toLowerCase();
        const firstKey = first.toLowerCase();
  
        const idFull = resolveId(fullKey) || resolveId(raw);
        const idFirst = resolveId(firstKey) || resolveId(first);
  
        if (idFull) {
          // Full two-word name resolves: use "rachel park" as the mention
          display = raw;
          id = idFull;
        } else if (idFirst) {
          // Only first word resolves: use "@dory" as mention, leave " hiii" as text
          display = first;
          trailing = rest; // e.g. " hiii"
          id = idFirst;
        } else {
          // Neither resolves: still treat only first word as the mention, rest as text
          display = first;
          trailing = rest;
          const key = firstKey;
          id = resolveId(key) || resolveId(first);
        }
      } else {
        // Single word or quoted name
        const key = raw.toLowerCase();
        id = resolveId(key) || resolveId(raw);
      }
  
      nodes.push(
        <MentionLink key={`cm-${display}-${start}`} id={id} handle={display}>
          @{display}
        </MentionLink>
      );
  
      if (trailing) {
        nodes.push(trailing);
      }
  
      last = end;
    }
  
    if (last < s.length) nodes.push(s.slice(last));
    return nodes;
  };

  const startMobileLongPress = (commentId, isMine) => {
    if (readOnly || !isMine) return;

    longPressTimerRef.current = window.setTimeout(() => {
      setMobileDeleteFor(commentId);
    }, 450);
  };

  const cancelMobileLongPress = () => {
    if (longPressTimerRef.current) {
      window.clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  };
    
  return (
    <div className={`mt-3 ${className}`}>
      {error && <div className="mt-2 text-sm text-red-600">{String(error)}</div>}

      <div className="space-y-3 sm:space-y-4">
        {visibleComments.length === 0 ? (
          <div className="text-xs ml-3 text-gray-500">Be the first to comment!</div>
        ) : (
          visibleComments.map((c) => {
            const isMine = c?.user?.id && currentUser?.id && c.user.id === currentUser.id;
            const stars = c.star_count || 0;
            const label = stars === 0 ? "" : stars === 1 ? "1 Star" : `${stars} Stars`;

            return (
              <div
                key={c.id}
                className="group relative flex items-start gap-2.5"
                onTouchStart={() => startMobileLongPress(c.id, isMine)}
                onTouchEnd={cancelMobileLongPress}
                onTouchCancel={cancelMobileLongPress}
              >
                <div className="shrink-0 self-start">
                  <Avatar
                    src={c?.user?.profile_pic_url}
                    alt={c?.user?.name}
                  />
                </div>

                <div className="min-w-0 flex-1">
                  <div className="flex items-start justify-between gap-3">
                    {c?.user?.id && !readOnly ? (
                      <Link
                        to={`/profile/${c.user.id}`}
                        className="
                          min-w-0 truncate
                          text-sm font-semibold leading-snug
                          text-gray-900 hover:text-blue-700
                        "
                        title={`View ${c?.user?.name || "profile"}`}
                      >
                        {c?.user?.name || `User #${c.user.id}`}
                      </Link>
                    ) : (
                      <span className="min-w-0 truncate text-sm font-semibold leading-snug text-gray-900">
                        {c?.user?.name ||
                          (c?.user?.id ? `User #${c.user.id}` : "User")}
                      </span>
                    )}

                    <div className="relative shrink-0 text-right">
                      <div
                        className="text-xs leading-snug text-gray-500"
                        title={
                          c?.created_at
                            ? new Date(c.created_at).toLocaleString()
                            : undefined
                        }
                      >
                        {fmtCommentTime(c?.created_at)}
                      </div>

                      {isMine && !readOnly && (
                        <button
                          onClick={() => handleDelete(c.id)}
                          className="
                            hidden sm:block
                            absolute right-0 top-full mt-0.5
                            bg-transparent p-0
                            text-xs text-red-500
                            opacity-0 group-hover:opacity-100
                            hover:underline
                            disabled:opacity-50
                          "
                          disabled={!!deletingIds[c.id]}
                          type="button"
                        >
                          {deletingIds[c.id] ? "deleting…" : "delete"}
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="text-sm leading-snug text-gray-900 whitespace-pre-wrap break-words">
                    {renderWithMentions(c?.content)}
                  </div>

                  <div className="flex items-center leading-none">
                    {readOnly ? (
                      <span
                        className="flex items-center justify-start pr-1 pt-1"
                        aria-hidden="true"
                      >
                        <StarFour
                          size={14}
                          weight={c.starred_by_me ? "fill" : "regular"}
                          className={c.starred_by_me ? "text-blue-600" : "text-gray-800"}
                        />
                      </span>
                    ) : (
                      <button
                        type="button"
                        onClick={() => toggleStar(c)}
                        className="flex items-center justify-start pr-1 !min-w-0 !min-h-0 pt-1 rounded-full"
                        aria-pressed={!!c.starred_by_me}
                        title={c.starred_by_me ? "Unstar" : "Star"}
                      >
                        <StarFour
                          size={14}
                          weight={c.starred_by_me ? "fill" : "regular"}
                          className={c.starred_by_me ? "text-blue-600" : "text-gray-800"}
                        />
                      </button>
                    )}

                    {(c.star_count || 0) > 0 &&
                      (readOnly ? (
                        <span className="sm:px-1 pt-1 text-xs text-blue-700">
                          {label}
                        </span>
                      ) : (
                        <button
                          type="button"
                          onClick={() => openStars(c.id)}
                          className="bg-transparent text-start !min-w-0 !min-h-0 sm:px-1 pt-1 text-xs text-blue-700 hover:font-bold"
                        >
                          {label}
                        </button>
                      ))}

                    {!readOnly && (
                      <button
                        type="button"
                        onClick={() => handleReply(c?.user)}
                        className="bg-transparent !min-w-0 !min-h-0 px-2 pt-1 text-xs text-gray-500 hover:text-gray-700 hover:font-semibold"
                      >
                        Reply
                      </button>
                    )}
                  </div>
                </div>

                {!readOnly && isMine && mobileDeleteFor === c.id && (
                  <>
                    <button
                      type="button"
                      aria-label="Close delete popup"
                      onClick={() => setMobileDeleteFor(null)}
                      className="sm:hidden fixed inset-0 z-40 bg-black/5 backdrop-blur-[1px]"
                    />

                    <div className="sm:hidden absolute rounded-full right-0 top-6 z-50 border border-gray-200 bg-white shadow-lg overflow-hidden">
                      <button
                        type="button"
                        onClick={() => {
                          setMobileDeleteFor(null);
                          handleDelete(c.id);
                        }}
                        disabled={!!deletingIds[c.id]}
                        className="block w-full px-4 !min-h-0 !h-8 text-left text-sm font-medium text-red-600 bg-white disabled:opacity-50"
                      >
                        {deletingIds[c.id] ? "deleting…" : "Delete"}
                      </button>
                    </div>
                  </>
                )}
              </div>
            );
          })
        )}

        {comments.length > 2 && (
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="text-sm ml-1 !min-w-0 !min-h-0 text-gray-500 hover:text-gray-700 underline"
          >
            {expanded ? "Hide comments" : `See all ${comments.length} comments`}
          </button>
        )}
      </div>
      {!readOnly && (
        <form
          onSubmit={handlePost}
          className="flex gap-2 mt-2 items-center"
          style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
        >
          <MentionTextarea
            id={textareaId}
            value={value}
            onChange={setValue}
            placeholder="Write a comment"
            className="flex-1"
            rows={1}
          />

          <button
            type="submit"
            disabled={posting}
            className="bg-blue-600 hover:ring-2 hover:ring-blue-400 text-white text-sm rounded-full disabled:opacity-50 flex items-center justify-center overflow-hidden leading-none !min-h-0 !min-w-0 !px-3 !py-2"
          >
            {posting ? "Posting…" : "Post"}
          </button>
        </form>
      )}

      {/* ---------- Stars Modal ---------- */}
      {starModalFor != null && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
          style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
        >
          <div
            className="bg-white w-full max-w-[350px] rounded-2xl shadow-[0_6px_0px_rgba(37,99,235,0.96)] py-3 px-5"
            role="dialog"
            aria-modal="true"
          >
            <div className="flex items-center justify-between">
              <h4 className="font-semibold text-blue-700">Stars</h4>
              <button
                type="button"
                onClick={closeStars}
                aria-label="Close"
                className="!min-h-0 !h-7 !min-w-0 !py-0"
              >
                <X size={16} weight="bold" />
              </button>
            </div>

            {starLoading ? (
              <div className="py-6 text-sm text-gray-600">Loading…</div>
            ) : starErr ? (
              <div className="py-4 text-sm text-red-600">{starErr}</div>
            ) : starUsers.length === 0 ? (
              <div className="py-6 text-sm text-gray-600">No stars yet.</div>
            ) : (
              <ul className="max-h-72 overflow-auto" style={{ WebkitOverflowScrolling: "touch" }}>
                {starUsers.map((u) => {
                  const id = u?.id;
                  const name = u?.name || u?.display_name || u?.username || (id ? `User #${id}` : "User");
                  return (
                    <li key={id || name} className="py-2 flex items-center gap-3">
                      <img
                        src={u?.profile_pic_url || DEFAULT_PROFILE_IMAGE}
                        alt={name}
                        className="w-9 h-9 rounded-full object-cover"
                        loading="lazy"
                        decoding="async"
                      />
                      {id && !readOnly ? (
                        <Link
                          to={`/profile/${id}`}
                          className="text-sm text-gray-800 hover:text-blue-600 hover:font-bold"
                        >
                          {name}
                        </Link>
                      ) : (
                        <span className="text-sm text-gray-800">{name}</span>
                      )}
                      {u?.username && (
                        <span className="text-xs text-gray-500">@{u.username}</span>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CommentsSection;
